import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Ban Watch Candidates';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.BulkCandidateTools,
	portal: 'SP',
};

const htmlContent = (invalidateColumn: string = '') => `
<p>
	Follow the instructions below to format a CSV file that will allow you to ban/watch candidates
	in bulk. Select the CSV file then press the Submit button to receive a CSV file with candidate
	and status information for each exam candidate ID.
</p>
<p>Note: There is a 500 row limit.</p>
<p><strong>To create the Comma Separated File (CSV) import file:</strong></p>
<p>
	Include the following column headers recognized by the parser. All fields are required for a
	successful upload. Extra fields will be ignored. Omitting the required columns will result in an
	error.
</p>
<ul>
	<li>Candidate ID</li>
	<li>Banned End Date:  accepted values are a valid future date string, remove ban, no action.</li>
	<li>Watch End Date:  accepted values are a valid future date string, remove watch, no action.</li>
	<li>Note</li>
	${invalidateColumn}
	<li>Testing Restricted:  Yes/No, if left blank, that will mean no action is taken to change the existing restriction status.</li>
	<li>Token1 - Token4: Optional value to pull into 'Restrict Testing' email. Character limit of 1,024 per token.</li>
	<li>Token5 - Token54: Optional value to pull into 'Ban' email. Character limit of 1,024 per token.</li>
</ul>
<p>
	Below the column header lines, add a line for each candidate ID requiring a ban/watch status
	change.
</p>
<p><strong>Return CSV file:</strong></p>

<p>After pressing the Submit button, a CSV file will be returned with following columns:</p>
<ul>
	<li>Candidate ID</li>
	<li>Banned End Date</li>
	<li>Watch End Date</li>
	<li>Note</li>
	<li>Update Status</li>
</ul>
<br>
<p>The status field will contain one of these values: </p>

<ul>
	<li>Banned</li>
	<li>Watched</li>
	<li>Not found - Candidate ID provided was not found.</li>
</ul>
`;

export type SPBanWatchCandidatesLocNames =
	| 'SPBanWatchCandidatesPageTitle'
	| 'SPBanWatchCandidatesPageHeading'
	| 'SPBanWatchCandidatesButton'
	| 'SPBanWatchCandidatesInstructions'
	| 'SPBanWatchCandidatesInstructionsWithInvalidate'
	| 'SPBanWatchCandidatesParseError'
	| 'SPBanWatchCandidatesMaxRowsError'
	| 'SPBanWatchCandidatesCurrentImports'
	| 'SPBanWatchCandidatesStatusProcessing'
	| 'SPBanWatchCandidatesStatusCompleted'
	| 'SPBanWatchCandidatesStatusFailed'
	| 'SPBanWatchCandidatesUploadQueuedSuccess'
	| 'SPBanWatchCandidatesNoResults'
	| 'SPBanWatchCandidatesDownloadError'
	| 'SPBanWatchCandidatesDownloadTooltip'
	| 'SPBanWatchCandidatesColumnFileName'
	| 'SPBanWatchCandidatesColumnStatus'
	| 'SPBanWatchCandidatesColumnDate';

export const spBanWatchCandidatesLocs: LocDefs<SPBanWatchCandidatesLocNames> = {
	SPBanWatchCandidatesPageTitle: {
		defaultValue: 'Ban Watch Candidates',
		description: 'Page title for the ban watch candidates page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPBanWatchCandidatesPageHeading: {
		defaultValue: 'Candidate Tools',
		description: 'Page Heading for the ban watch candidates page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPBanWatchCandidatesInstructions: {
		defaultValue: htmlContent(),
		description: 'Instructions for the ban watch candidates page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPBanWatchCandidatesInstructionsWithInvalidate: {
		defaultValue: htmlContent('<li>Invalidate: Yes/No, if left blank will default to No.</li>'),
		description:
			'Instructions for the ban watch candidates page when the invalidate column is enabled.',
		displayPath: `${dpRoot}|Instructions with Invalidate`,
		displayType: 'Content',
		shared: false,
		feature: HubFeature.BanAndInvalidateTool,
		portal: 'SP',
		htmlAllowed: true,
	},
	SPBanWatchCandidatesButton: {
		defaultValue: 'Update Status',
		description: 'Button to update candidate status',
		displayPath: `${dpRoot}|Update Status Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPBanWatchCandidatesParseError: {
		defaultValue:
			'Unable to parse the CSV file. Please ensure the file is formatted as per instructions.',
		description: 'Notification message when CSV parse fails',
		displayPath: `${dpRoot}|CSV parse error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBanWatchCandidatesMaxRowsError: {
		defaultValue: 'The CSV file cannot contain more than 500 updates.',
		description: 'Notification message when CSV exceeds the maximum number of rows',
		displayPath: `${dpRoot}|Max rows error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBanWatchCandidatesCurrentImports: {
		defaultValue: 'Current Imports',
		description: 'Title for the current imports section',
		displayPath: `${dpRoot}|Current Imports`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPBanWatchCandidatesStatusProcessing: {
		defaultValue: 'Processing',
		description: 'Status label for processing jobs',
		displayPath: `${dpRoot}|Status Processing`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPBanWatchCandidatesStatusCompleted: {
		defaultValue: 'Completed',
		description: 'Status label for completed jobs',
		displayPath: `${dpRoot}|Status Completed`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPBanWatchCandidatesStatusFailed: {
		defaultValue: 'Failed',
		description: 'Status label for failed jobs',
		displayPath: `${dpRoot}|Status Failed`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPBanWatchCandidatesUploadQueuedSuccess: {
		defaultValue: 'Upload queued successfully',
		description: 'Success message when upload is queued',
		displayPath: `${dpRoot}|Upload Queued Success`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPBanWatchCandidatesNoResults: {
		defaultValue: 'No results available for download',
		description: 'Error message when no results are available',
		displayPath: `${dpRoot}|No Results`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBanWatchCandidatesDownloadError: {
		defaultValue: 'Failed to download results',
		description: 'Error message when download fails',
		displayPath: `${dpRoot}|Download Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBanWatchCandidatesDownloadTooltip: {
		defaultValue: 'Download Results',
		description: 'Tooltip for download button',
		displayPath: `${dpRoot}|Download Tooltip`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPBanWatchCandidatesColumnFileName: {
		defaultValue: 'File Name',
		description: 'Column header for file name',
		displayPath: `${dpRoot}|Column File Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPBanWatchCandidatesColumnStatus: {
		defaultValue: 'Status',
		description: 'Column header for status',
		displayPath: `${dpRoot}|Column Status`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPBanWatchCandidatesColumnDate: {
		defaultValue: 'Date',
		description: 'Column header for date',
		displayPath: `${dpRoot}|Column Date`,
		displayType: 'Label',
		...sharedProperties,
	},
};

export const BAN_WATCH_CANDIDATE_LOC_KEYS =
	MapLocKeys<SPBanWatchCandidatesLocNames>(spBanWatchCandidatesLocs);
export type SPBanWatchCandidatesLocType = LocMap<SPBanWatchCandidatesLocNames>;
